import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { motion, useMotionValue, useTransform } from "framer-motion";
import classNames from "classnames/bind";
import useResizeObserver from "use-resize-observer";

import styles from "./RatingScale.module.scss";

const cx = classNames.bind(styles);

export interface RatingScaleProps {
  min: number;
  max: number;
  value: number;
  className?: string;
  onChange(value: number): void;
}

const thumbColors = [
  "rgb(176, 53, 194)",
  "rgb(116, 201, 255)",
  "rgb(156, 165, 66)",
  "rgb(242, 106, 24)",
  "rgb(255, 54, 82)",
];

const trackOffset = 24;

type Step = {
  value: number;
  start: number;
  end: number;
};

const countStepCenter = (step: Step) => (step.start + step.end) / 2 - trackOffset;

export const RatingScale = ({ min, max, value, className, onChange }: RatingScaleProps) => {
  const { ref: resizeRef, width: trackWidth = 0 } = useResizeObserver();
  const [internalValue, setInternalValue] = useState(value);
  const debouncedOnChange = useMemo(() => debounce(onChange, 100), [onChange]);

  const sliderValue = useMotionValue(trackOffset);

  const thumbColor = useTransform(
    sliderValue,
    thumbColors.map((_, index) => index * ((trackWidth - trackOffset) / thumbColors.length) - trackOffset),
    thumbColors,
  );

  const stepsAmount = max - min + 1;

  const steps: Step[] = useMemo(
    () =>
      Array.from({ length: stepsAmount }, (_, index) => ({
        value: index + min,
        start: index * (trackWidth / stepsAmount),
        end: (index + 1) * (trackWidth / stepsAmount),
      })),
    [trackWidth, stepsAmount],
  );
  const currentStep = useMemo(() => steps.find(({ value: v }) => v === value) ?? steps[0], [value, steps]);

  const stepObserver = useTransform(sliderValue, (value) => {
    const centralValue = value + trackOffset;
    let newInternalValue = steps.find(({ start, end }) => centralValue >= start && centralValue <= end)?.value;
    if (centralValue <= -trackOffset) {
      newInternalValue = min;
    } else if (centralValue > trackWidth - trackOffset) {
      newInternalValue = max;
    }
    return newInternalValue;
  });

  useEffect(
    () =>
      stepObserver.onChange((latest) => {
        setInternalValue(latest ?? min);
        debouncedOnChange(latest ?? min);
      }),
    [],
  );

  useEffect(() => {
    if (value !== internalValue) {
      sliderValue.set(countStepCenter(currentStep));
    }
  }, [value]);

  useEffect(() => {
    sliderValue.set(countStepCenter(currentStep));
  }, [trackWidth]);

  return (
    <div ref={resizeRef} className={cx("ratingScale", className)}>
      <input
        type="range"
        min={-trackOffset}
        max={trackWidth - trackOffset}
        step="1"
        defaultValue={max}
        className={styles.slider}
        onInput={(event: ChangeEvent<HTMLInputElement>) => {
          sliderValue.set(Number(event.currentTarget.value));
        }}
      />
      <motion.div
        drag="x"
        className={styles.thumb}
        dragTransition={{
          bounceStiffness: 400,
          bounceDamping: 20,
        }}
        dragElastic={false}
        dragMomentum={false}
        style={{
          x: sliderValue,
          backgroundColor: thumbColor,
        }}
        dragConstraints={{ left: -trackOffset, right: trackWidth - trackOffset }}
      />
      {steps.map(({ value, end }) => {
        return (
          <div
            key={value}
            className={styles.trackMarker}
            style={{
              left: end - 2,
            }}
          />
        );
      })}
    </div>
  );
};
